import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { PlayCircle, YoutubeLogo } from "@phosphor-icons/react";
import { Reveal, SectionHead } from "./Reveal";
import { EPISODES, YOUTUBE_CHANNEL_URL } from "@/data/content";
import { useLanguage } from "@/i18n";

const maxres = (id) => `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
const hq = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATIC_BY_ID = Object.fromEntries(EPISODES.map((e) => [e.id, e]));

export default function VideoSection() {
  const { lang, t } = useLanguage();
  const [episodes, setEpisodes] = useState(EPISODES);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const video = episodes[current];

  useEffect(() => {
    axios
      .get(`${API}/episodes`)
      .then((r) => {
        if (!Array.isArray(r.data) || !r.data.length) return;
        const merged = r.data.map(
          (v) => STATIC_BY_ID[v.id] || { id: v.id, ep: { en: "New Episode", ar: "حلقة جديدة" }, title: { en: v.title, ar: v.title } }
        );
        setEpisodes(merged);
        setCurrent(0);
        setPlaying(false);
      })
      .catch(() => {});
  }, []);

  const select = (i) => {
    setCurrent(i);
    setPlaying(true);
  };

  return (
    <section id="video" data-testid="video-section" className="mx-auto max-w-6xl px-6 py-24 md:px-12 md:py-36">
      <SectionHead index={t.video.index} overline={t.video.overline} title={t.video.title} align="center" testid="video" />
      <Reveal delay={0.1}>
        <p data-testid="video-tagline" className="mt-6 text-center font-serif text-xl italic text-gold-gradient md:text-2xl">
          {t.video.tagline}
        </p>
      </Reveal>
      <Reveal delay={0.15} className="mt-16">
        <div className="group relative overflow-hidden border border-gold/25" data-testid="video-stage">
          <AnimatePresence mode="wait">
            {playing ? (
              <motion.iframe
                key={video.id}
                data-testid="video-iframe"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="aspect-video w-full"
                src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
                title={video.title[lang]}
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <motion.div key={`poster-${video.id}`} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                <img
                  src={maxres(video.id)}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = hq(video.id);
                  }}
                  alt={video.title[lang]}
                  className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-charcoal/50 transition-colors duration-500 group-hover:bg-charcoal/40" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.button
                    data-testid="video-play-button"
                    onClick={() => setPlaying(true)}
                    whileHover={{ scale: 1.12 }}
                    whileTap={{ scale: 0.95 }}
                    className="rounded-full border border-gold/60 bg-charcoal/60 p-4 text-gold backdrop-blur-sm transition-colors duration-300 hover:bg-gold hover:text-charcoal"
                    aria-label={t.video.play}
                  >
                    <PlayCircle size={56} weight="duotone" />
                  </motion.button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-charcoal to-transparent p-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80">{video.ep[lang]}</p>
                  <p data-testid="video-stage-title" className="mt-1 font-serif text-lg text-[#FAFAFA] md:text-xl">{video.title[lang]}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Reveal>

      <Reveal delay={0.25}>
        <p className="mt-10 text-center text-[10px] uppercase tracking-[0.35em] text-gold/70">{t.video.allEps}</p>
        <div data-testid="episode-strip" className="mt-6 flex gap-4 overflow-x-auto pb-4 [scrollbar-width:thin]">
          {episodes.map((e, i) => (
            <button
              key={e.id}
              data-testid={`episode-card-${i}`}
              onClick={() => select(i)}
              className={`group w-60 shrink-0 border text-left transition-all duration-500 hover:-translate-y-1 rtl:text-right ${
                i === current ? "border-gold/70 shadow-lg shadow-gold/10" : "border-gold/15 hover:border-gold/50"
              }`}
            >
              <div className="relative overflow-hidden">
                <img src={hq(e.id)} alt={e.title[lang]} loading="lazy" className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                {i === current && <span className="absolute left-2 top-2 bg-gold px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.2em] text-charcoal">Now</span>}
              </div>
              <div className="bg-surface p-4">
                <p className="text-[9px] uppercase tracking-[0.3em] text-gold">{e.ep[lang]}</p>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-[#A3A3A3]">{e.title[lang]}</p>
              </div>
            </button>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-2xl text-center leading-relaxed text-[#A3A3A3]">{t.video.desc}</p>
        <div className="mt-8 flex justify-center">
          <a
            data-testid="youtube-subscribe-button"
            href={YOUTUBE_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 border border-gold/50 px-6 py-3 text-[11px] uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-charcoal"
          >
            <YoutubeLogo size={18} weight="duotone" /> {t.video.subscribe}
          </a>
        </div>
      </Reveal>
    </section>
  );
}
